/**
 * Main Application Component
 * Sets up routing with React Router and protected routes
 */

import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toast } from './components/Toast';
import { AppLayout } from './components/AppLayout';
import { PickupLocations } from './pages/admin/PickupLocations';
import { Members } from './pages/admin/Members';
import { Menus } from './pages/admin/Menus';
import { Feedback } from './pages/admin/Feedback';
import { Reports } from './pages/admin/Reports';
import { SystemSettings } from './pages/admin/SystemSettings';
import { RSVP } from './pages/RSVP';
import { PublicRSVP } from './pages/PublicRSVP';
import { PublicRSVPManage } from './pages/PublicRSVPManage';
import { Profile } from './pages/Profile';
import { ProcurementPlanning } from './pages/admin/ProcurementPlanning';
import { KitchenLibrary } from './pages/admin/KitchenLibrary';
import { CookingHelperSchedule } from './pages/admin/CookingHelperSchedule';
import { TooltipProvider } from './components/ui/tooltip';
import './index.css';

export function App() {
  return (
    <TooltipProvider>
      <BrowserRouter>
        <Toast />
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/public-rsvp/:slug" element={<PublicRSVP />} />
        <Route path="/public-rsvp/manage/:token" element={<PublicRSVPManage />} />

        {/* Protected routes - any authenticated user */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout><RSVP /></AppLayout>} path="/rsvp" />
          <Route element={<AppLayout><Profile /></AppLayout>} path="/profile" />
        </Route>

        {/* Admin-only routes */}
        <Route element={<ProtectedRoute requiredRole="ADMIN" />}>
          <Route element={<AppLayout><AdminDashboard /></AppLayout>} path="/admin/dashboard" />
          <Route element={<AppLayout><PickupLocations /></AppLayout>} path="/admin/pickup-locations" />
          <Route element={<AppLayout><Members /></AppLayout>} path="/admin/members" />
          <Route element={<AppLayout><Menus /></AppLayout>} path="/admin/menus" />
          <Route element={<AppLayout><ProcurementPlanning /></AppLayout>} path="/admin/procurement" />
          <Route element={<AppLayout><KitchenLibrary section="vendors" /></AppLayout>} path="/admin/library/vendors" />
          <Route element={<AppLayout><Feedback /></AppLayout>} path="/admin/feedback" />
          <Route element={<AppLayout><Reports /></AppLayout>} path="/admin/reports" />
          <Route element={<AppLayout><SystemSettings /></AppLayout>} path="/admin/system-settings" />
        </Route>

        {/* Admin + Caterer routes */}
        <Route element={<ProtectedRoute requiredRole={['ADMIN', 'CATERER']} />}>
          <Route element={<Navigate to="/admin/library/recipes" replace />} path="/admin/library" />
          <Route element={<AppLayout><KitchenLibrary section="recipes" /></AppLayout>} path="/admin/library/recipes" />
          <Route element={<AppLayout><KitchenLibrary section="ingredients" /></AppLayout>} path="/admin/library/ingredients" />
          <Route element={<AppLayout><CookingHelperSchedule /></AppLayout>} path="/kitchen/helper-schedule" />
        </Route>

        {/* Caterer-only routes */}
        <Route element={<ProtectedRoute requiredRole="CATERER" />}>
          <Route element={<AppLayout><CatererDashboard /></AppLayout>} path="/caterer/*" />
          <Route element={<AppLayout><ProcurementPlanning /></AppLayout>} path="/caterer/procurement" />
          <Route element={<AppLayout><Feedback /></AppLayout>} path="/caterer/feedback" />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/rsvp" replace />} />

        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/rsvp" replace />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  );
}

/**
 * Placeholder components for routes (TODO)
 */
function AdminDashboard() {
  return (
    <div className="container mx-auto p-8">
      <p className="text-muted-foreground mb-6">Manage users, locations, and system settings.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link
          to="/admin/pickup-locations"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Pickup Locations</h3>
          <p className="text-sm text-muted-foreground">Manage meal pickup locations</p>
        </Link>

        <Link
          to="/admin/members"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Members</h3>
          <p className="text-sm text-muted-foreground">Manage community members</p>
        </Link>

        <Link
          to="/admin/menus"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Menus</h3>
          <p className="text-sm text-muted-foreground">Create and manage daily menus</p>
        </Link>

        <Link
          to="/admin/library/recipes"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Kitchen Library</h3>
          <p className="text-sm text-muted-foreground">Manage recipes, ingredients, and vendors in one workspace</p>
        </Link>

        <Link
          to="/admin/procurement"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Procurement</h3>
          <p className="text-sm text-muted-foreground">Generate and adjust weekly shopping lists</p>
        </Link>

        <Link
          to="/kitchen/helper-schedule"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Helper Schedule</h3>
          <p className="text-sm text-muted-foreground">Generate next week helper timings and copy to WhatsApp</p>
        </Link>

        <Link
          to="/admin/feedback"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Feedback</h3>
          <p className="text-sm text-muted-foreground">View member feedback on meals</p>
        </Link>

        <Link
          to="/admin/reports"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">RSVP Reports</h3>
          <p className="text-sm text-muted-foreground">View RSVP summaries and export data</p>
        </Link>

        <Link
          to="/admin/system-settings"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">System Settings</h3>
          <p className="text-sm text-muted-foreground">Configure RSVP cutoff rules</p>
        </Link>
      </div>
    </div>
  );
}

function CatererDashboard() {
  return (
    <div className="container mx-auto p-8">
      <p className="text-muted-foreground mb-6">Plan weekly procurement and review kitchen feedback.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/caterer/procurement"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Procurement</h3>
          <p className="text-sm text-muted-foreground">Generate and edit the weekly procurement list</p>
        </Link>

        <Link
          to="/kitchen/helper-schedule"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Helper Schedule</h3>
          <p className="text-sm text-muted-foreground">Generate next week cooking-helper schedule text</p>
        </Link>

        <Link
          to="/caterer/feedback"
          className="block p-6 border rounded-lg hover:bg-accent transition-colors"
        >
          <h3 className="text-lg font-semibold mb-2">Feedback</h3>
          <p className="text-sm text-muted-foreground">Review member feedback on meals</p>
        </Link>
      </div>
    </div>
  );
}

export default App;
